/**
 * Pure, exported helper functions for the XEPPT Connect popup.
 * All security-critical logic lives here so it is trivially unit-testable.
 *
 * SECURITY CONTRACT:
 *  - postSuccessMessage() ONLY posts to the authoritative backend-returned redirect_origin.
 *  - postCancelMessage() posts only to a validated https origin, or skips posting entirely.
 *  - targetOrigin is NEVER '*'.
 *  - The one-time `code` is never logged or sent to the `o` URL hint.
 */

const STRIP_TAGS_RE = /<[^>]*>/g;
const MAX_MERCHANT_NAME_LENGTH = 100;

/**
 * Validates that a value is an https:// origin (scheme + host + optional port, no path).
 * Returns the normalized origin string if valid, null otherwise.
 */
export function validateHttpsOrigin(input: unknown): string | null {
    if (typeof input !== 'string' || input.trim() === '') return null;
    try {
        const url = new URL(input.trim());
        if (url.protocol !== 'https:') return null;
        const origin = url.origin;
        if (origin === 'null') return null;
        return origin;
    } catch {
        return null;
    }
}

/**
 * Sanitizes an untrusted merchant display name for safe UI rendering.
 * Strips HTML tags and caps length to prevent XSS and UI overflow.
 */
export function sanitizeMerchantName(input: unknown): string {
    if (typeof input !== 'string') return '';
    return input.replace(STRIP_TAGS_RE, '').slice(0, MAX_MERCHANT_NAME_LENGTH).trim();
}

/**
 * Parses the opaque `token` query parameter from a URL search string.
 * Returns the raw string or null if absent.
 */
export function parseConnectToken(search: string): string | null {
    const params = new URLSearchParams(search);
    const token = params.get('token');
    return token && token.trim().length > 0 ? token.trim() : null;
}

/**
 * Parses the `o` origin-hint query parameter and validates it as an https origin.
 * Returns the validated origin or null if missing or invalid.
 * This value is ONLY used as a cancel message target — NEVER for the success code.
 */
export function parseOriginHint(search: string): string | null {
    const params = new URLSearchParams(search);
    return validateHttpsOrigin(params.get('o'));
}

/**
 * Parses and sanitizes the `m` merchant-name hint query parameter.
 * The return value is display-only and must not be trusted for authorization.
 */
export function parseMerchantNameHint(search: string): string {
    const params = new URLSearchParams(search);
    return sanitizeMerchantName(params.get('m'));
}

export interface ConnectSuccessPayload {
    source: 'xeppt-connect';
    type: 'success';
    code: string;
}

export interface ConnectPaymentSuccessPayload {
    source: 'xeppt-connect';
    type: 'success';
    transaction_id: string;
    amount: number;
    // The paying customer's anonymous, stable, per-merchant id (scoped_id).
    customer_id?: string;
    // The paying customer's XEPPT @handle (username), when they have one.
    username?: string;
}

export interface ConnectCancelPayload {
    source: 'xeppt-connect';
    type: 'cancel';
}

function buildSuccessPayload(code: string): ConnectSuccessPayload {
    return { source: 'xeppt-connect', type: 'success', code };
}

function buildCancelPayload(): ConnectCancelPayload {
    return { source: 'xeppt-connect', type: 'cancel' };
}

/**
 * Posts the success message to the authoritative backend-returned redirect_origin.
 *
 * SECURITY: targetOrigin is always the validated backend origin — NEVER '*'.
 * The `o` URL hint is intentionally NOT used here; only the backend value is trusted.
 *
 * @throws if redirectOrigin does not pass https validation (programming error)
 */
export function postSuccessMessage(code: string, redirectOrigin: string): void {
    const validOrigin = validateHttpsOrigin(redirectOrigin);
    if (!validOrigin) {
        // redirectOrigin came from the backend and was already stored — if it fails
        // validation here something is seriously wrong; fail closed rather than fall back.
        throw new Error(`postSuccessMessage: invalid redirect_origin`);
    }
    window.opener?.postMessage(buildSuccessPayload(code), validOrigin);
}

/**
 * Posts the payment success message to the authoritative backend-returned redirect_origin.
 * Carries transaction_id and amount so the merchant can confirm without a server round-trip.
 *
 * SECURITY: targetOrigin is always the validated backend origin — NEVER '*'.
 *
 * @throws if redirectOrigin does not pass https validation (programming error)
 */
export function postPaymentSuccessMessage(
    transactionId: string,
    amount: number,
    redirectOrigin: string,
    customerId?: string,
    username?: string
): void {
    const validOrigin = validateHttpsOrigin(redirectOrigin);
    if (!validOrigin) {
        throw new Error(`postPaymentSuccessMessage: invalid redirect_origin`);
    }
    const payload: ConnectPaymentSuccessPayload = {
        source: 'xeppt-connect',
        type: 'success',
        transaction_id: transactionId,
        amount,
        ...(customerId ? { customer_id: customerId } : {}),
        ...(username ? { username } : {})
    };
    window.opener?.postMessage(payload, validOrigin);
}

/**
 * Posts the cancel message to a validated target origin.
 * Accepts either the authoritative backend origin or the validated `o` hint (in that order).
 * If neither is provided, posting is skipped — we never send to '*'.
 */
export function postCancelMessage(targetOrigin: string | null): void {
    if (!targetOrigin) return;
    const validOrigin = validateHttpsOrigin(targetOrigin);
    if (!validOrigin) return;
    window.opener?.postMessage(buildCancelPayload(), validOrigin);
}

/**
 * Builds a window.open features string for a checkout popup that is CENTERED
 * over the current (opener) window — exactly like PayPal's Smart Buttons popup.
 * Positioning it relative to the opener makes the popup feel attached to the
 * merchant page instead of appearing as a detached window in a random corner.
 * `popup=yes` also hints modern browsers to open a minimal window (not a tab).
 *
 * The default size fits the two-column XEPPT checkout card (max-width 862px ×
 * min-height 565px — the sign-in/pay form beside the illustration panel). The
 * card has a FIXED 423px form column and no narrow/stacked layout, so a window
 * smaller than the card crowds out the illustration column and clips the card
 * off the bottom. The popup must be wide enough to render the whole card at once.
 */
export function centeredPopupFeatures(width = 900, height = 640): string {
    // Center on the SCREEN using fixed screen geometry only — deliberately NOT
    // the browser window's position/size, which varies by setup and (in earlier
    // attempts) pushed the popup to the bottom or a corner. availWidth/Height
    // exclude the OS menu bar and dock; availLeft/availTop (non-standard but
    // supported in Chromium/Firefox) offset to the monitor the browser is on and
    // default to 0 (primary display). This can only ever produce a centered
    // window (or, on a display smaller than the popup, the top-left corner).
    const scr = window.screen as Screen & { availLeft?: number; availTop?: number };
    const availW = scr?.availWidth || window.innerWidth || width;
    const availH = scr?.availHeight || window.innerHeight || height;
    const offX = scr?.availLeft ?? 0;
    const offY = scr?.availTop ?? 0;
    const left = Math.round(offX + Math.max(0, (availW - width) / 2));
    const top = Math.round(offY + Math.max(0, (availH - height) / 2));
    return `popup=yes,width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes`;
}

/**
 * Derives 2-character uppercase initials from a first and last name.
 * Falls back to a single initial or '??' when names are empty.
 */
export function deriveInitials(firstName: string, lastName: string): string {
    const first = firstName.trim().charAt(0).toUpperCase();
    const last = lastName.trim().charAt(0).toUpperCase();
    if (!first && !last) return '??';
    if (!last) return `${first}${first}`;
    return `${first}${last}`;
}
